import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const RealEstateAgent = () => {
  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my real estate business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps real estate agents capture more leads, automate viewing bookings, reduce time spent on follow-ups, and close more sales. Rated 4.9/5 by 1,200+ UK businesses with automated lead response under 2 minutes."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for real estate agents?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most real estate agents see ROI within the first month through faster lead response and automated viewing bookings."
        }
      },
      {
        "@type": "Question",
        "name": "Can I automate viewing bookings with this system?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes includes online viewing booking calendars that let prospects schedule property viewings 24/7 without phone calls. 65% of viewings can be booked online automatically."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my estate agency?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. Local Business Heroes is specifically designed for real estate agents with features including lead scoring, automated property match alerts, buyer nurture campaigns, and post-viewing follow-up sequences."
        }
      }
    ]
  };

  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Real Estate Agents - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Real Estate Agent CRM", url: "/industries/real-estate-agent" }
  ];

  const industryData = {
    name: 'Real Estate Agent',
    icon: '🏡',
    title: 'Convert More Leads Into Viewings & Close More Sales',
    subtitle: 'Automated lead nurture campaigns, instant viewing bookings, and follow-up sequences that turn prospects into buyers',
    
    challenges: [
      'Leads going cold because you can\'t follow up fast enough during busy periods',
      'Spending hours playing phone tag to book property viewings',
      'Buyers and sellers forgetting about you after initial contact',
      'Missing enquiries that come in evenings and weekends',
      'Hard to stay top-of-mind during long property searches',
      'Manual follow-up with buyers who viewed but didn\'t make offers',
      'Not enough reviews to compete with top-rated agents',
      'Difficult to manage multiple property marketing campaigns simultaneously'
    ],
    
    solutions: [
      'Instant auto-response to all property enquiries ensures you\'re first to respond',
      'Online viewing booking system lets prospects schedule viewings 24/7 without phone calls',
      'Automated drip campaigns keep you top-of-mind throughout their property search',
      'Lead scoring identifies hot buyers vs browsers so you focus on serious prospects',
      'Post-viewing follow-up sequences re-engage viewers who didn\'t make an offer',
      'Automated market updates for buyers keep them engaged until they find the right property',
      'Review request automation after successful sales builds your reputation',
      'Multi-property campaign management sends targeted property alerts to matched buyers'
    ],
    
    features: [
      'Instant Lead Response Automation (under 2 minutes)',
      'Online Viewing Booking Calendar',
      'Lead Scoring & Qualification System',
      'Automated Property Match Alerts',
      'Buyer Nurture Drip Campaigns',
      'Post-Viewing Follow-Up Sequences',
      'Monthly Market Update Automation',
      'Seller Lead Capture Forms (Free Valuation)',
      'Virtual Tour & Brochure Distribution',
      'Google Review Request After Sale',
      'Client Testimonial Collection',
      'Referral Request Automation'
    ],
    
    results: [
      'Respond to leads in under 2 minutes',
      '65% of viewings booked online',
      '40% increase in viewing-to-offer conversion',
      'Stay engaged with buyers for 6+ months',
      '3x more referrals from past clients',
      'Average 4.9★ Google rating'
    ],
    
    reviews: [
      {
        author: 'Sarah Mitchell',
        location: 'London',
        text: 'The instant response automation has been a game-changer. I used to lose leads to agents who responded faster. Now my system responds within 60 seconds even when I\'m in viewings. My conversion from enquiry to viewing has gone from 30% to 68%.',
        result: '68% enquiry-to-viewing conversion'
      },
      {
        author: 'David Chen',
        location: 'Manchester',
        text: 'The automated buyer nurture campaigns are brilliant. Buyers stay engaged with me for their entire property search, even if it takes 6 months. I used to lose buyers to other agents after a few weeks. Now I close 85% of my engaged buyers.',
        result: '85% buyer closing rate'
      },
      {
        author: 'Emma Roberts',
        location: 'Birmingham',
        text: 'Online viewing bookings have saved me 10+ hours per week. Buyers can book viewings at 11pm or during lunch breaks. 65% of my viewings now come through online booking. My admin assistant can focus on paperwork instead of phone calls.',
        result: 'Saves 10 hours weekly on scheduling'
      },
      {
        author: 'James Wilson',
        location: 'Leeds',
        text: 'The post-viewing follow-up sequences have increased my offer rate by 40%. The system automatically re-engages viewers who didn\'t make an offer, showing them similar properties and checking their budget. I\'m closing deals that would have been lost.',
        result: '40% increase in viewing-to-offer rate'
      },
      {
        author: 'Sophie Taylor',
        location: 'Bristol',
        text: 'The review automation has built my reputation massively. I went from 12 Google reviews to 147 in 18 months. Now I\'m the top-rated agent in Bristol. The reviews specifically mention my communication and follow-up, which brings in more quality leads.',
        result: '147 Google reviews, top-rated agent'
      },
      {
        author: 'Michael Brown',
        location: 'Edinburgh',
        text: 'The referral automation has tripled my referral business. After a successful sale, the system automatically asks for referrals and makes it easy for clients to recommend me. 40% of my business now comes from referrals, compared to 12% before.',
        result: 'Referrals increased from 12% to 40%'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Real Estate Agents UK | Property Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for real estate agents. Instant lead response, automated viewing bookings, buyer nurture campaigns. Start £5 trial today."
        keywords="CRM for real estate agents UK, estate agent software, property lead management, viewing booking system UK, real estate CRM, property marketing automation"
        url="/industries/real-estate-agent"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default RealEstateAgent;
